// COMPLIANCE: STATUS_COLORS violation detected - requires manual review
import { EXPLORER_CURRENCY } from '@/lib/fxToUsd';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TYPOGRAPHY, RESPONSIVE, TABS, ICONS } from '@/design-system';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TaxResult } from '@/types/taxExplorer';
import { X, TrendingDown, TrendingUp, Check, AlertCircle, Scale } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocalizations } from '@/hooks/useLocalizations';
import { LComponents } from '@/components/i18n';
import { useLocale } from '@/contexts/LocaleContext';

interface TaxExplorerComparisonProps {
  results: TaxResult[];
  currentTax: number;
  onUnpinCountry: (locationId: number) => void;
}

// Flag emoji helper
function getFlagEmoji(countryCode: string | null): string {
  if (!countryCode) return '🏳️';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

// Find best value for highlighting
const findBestValue = (results: TaxResult[], getValue: (r: TaxResult) => number, lowerIsBetter = true) => {
  if (results.length === 0) return null;
  const values = results.map(getValue);
  return lowerIsBetter ? Math.min(...values) : Math.max(...values);
};

export const TaxExplorerComparison = ({
  results,
  currentTax,
  onUnpinCountry
}: TaxExplorerComparisonProps) => {
  const { lComponents, lCommon } = useLocalizations();
  const { formatCompactCurrency, formatPercent } = useLocale();
  
  // Find best values for highlighting
  const bestEffectiveRate = findBestValue(results, r => r.effectiveRate);
  const bestSavings = findBestValue(results, r => r.savingsVsCurrent, false);
  const bestAspirationScore = findBestValue(results, r => r.aspirationScore, false);

  const rows = [
    {
      labelKey: 'tax_explorer.comparison.effective_rate',
      labelDefault: 'Effective Rate',
      category: 'tax',
      getValue: (r: TaxResult) => formatPercent(r.effectiveRate / 100, 0),
      isBest: (r: TaxResult) => r.effectiveRate === bestEffectiveRate
    },
    {
      labelKey: 'tax_explorer.comparison.total_tax',
      labelDefault: 'Total Tax',
      category: 'tax',
      getValue: (r: TaxResult) => formatCompactCurrency(r.totalTax, EXPLORER_CURRENCY),
      isBest: () => false
    },
    {
      labelKey: 'tax_explorer.comparison.savings_vs_current',
      labelDefault: 'Savings vs Current',
      category: 'tax',
      getValue: (r: TaxResult) => (
        <span className={cn(
          'flex items-center gap-1',
          r.savingsVsCurrent > 0 ? 'text-green-600' : 'text-red-600'
        )}>
          {r.savingsVsCurrent > 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
          {r.savingsVsCurrent > 0 ? '+' : ''}{formatCompactCurrency(r.savingsVsCurrent, EXPLORER_CURRENCY)}
          <span className={cn(TYPOGRAPHY.caption)}>({r.savingsPercent > 0 ? '+' : ''}{formatPercent(r.savingsPercent / 100, 0)})</span>
        </span>
      ),
      isBest: (r: TaxResult) => r.savingsVsCurrent === bestSavings
    },
    {
      labelKey: 'tax_explorer.comparison.income_tax',
      labelDefault: 'Income Tax',
      category: 'breakdown',
      getValue: (r: TaxResult) => formatCompactCurrency(r.breakdown.incomeTax, EXPLORER_CURRENCY),
      isBest: () => false
    },
    {
      labelKey: 'tax_explorer.comparison.capital_gains_tax',
      labelDefault: 'Capital Gains Tax',
      category: 'breakdown',
      getValue: (r: TaxResult) => formatCompactCurrency(r.breakdown.capitalGainsTax, EXPLORER_CURRENCY),
      isBest: () => false
    },
    {
      labelKey: 'tax_explorer.comparison.wealth_tax',
      labelDefault: 'Wealth Tax',
      category: 'breakdown',
      getValue: (r: TaxResult) => r.breakdown.wealthTax > 0 ? formatCompactCurrency(r.breakdown.wealthTax, EXPLORER_CURRENCY) : lCommon('labels.none', { defaultValue: 'None' }),
      isBest: () => false
    },
    {
      labelKey: 'tax_explorer.comparison.residency_met',
      labelDefault: 'Residency Met',
      category: 'residency',
      getValue: (r: TaxResult) => r.residencyMet ? (
        <span className="flex items-center gap-1 text-success">
          <Check className="h-3 w-3" /> {lCommon('labels.yes', { defaultValue: 'Yes' })}
        </span>
      ) : (
        <span className="flex items-center gap-1 text-amber-600">
          <AlertCircle className="h-3 w-3" /> {lCommon('labels.no', { defaultValue: 'No' })}
        </span>
      ),
      isBest: () => false
    },
    {
      labelKey: 'tax_explorer.comparison.aspiration_match',
      labelDefault: 'Aspiration Match',
      category: 'lifestyle',
      getValue: (r: TaxResult) => (
        <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
          <Progress value={r.aspirationScore} className="h-2 w-16" />
          <span className="text-xs">{formatPercent(r.aspirationScore / 100, 0)}</span>
        </div>
      ),
      isBest: (r: TaxResult) => r.aspirationScore === bestAspirationScore
    }
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className={cn(RESPONSIVE.flexRowCenterGap2, 'text-lg')}>
          <Scale className={cn(ICONS.sectionHeader, "text-primary")} />
          <LComponents id="tax_explorer.comparison.title" defaultValue='Tax Comparison Overview' />
          <Badge variant="secondary" className="ml-2">
            {results.length}/5
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto -mx-1 px-1 pb-2">
          <table className="w-full text-sm min-w-[400px] sm:min-w-[500px]">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2 font-medium text-muted-foreground w-36">
                  <LComponents id="tax_explorer.comparison.metric" defaultValue='Metric' />
                </th>
                {results.map((result) => (
                  <th key={result.locationId} className="text-center p-2 min-w-[100px] sm:min-w-[140px]">
                    <div className="flex flex-col items-center gap-1">
                      <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
                        <span className="text-lg">{getFlagEmoji(result.countryCode)}</span>
                        <span className="font-semibold">{result.countryName}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => onUnpinCountry(result.locationId)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-b last:border-0">
                  <td className="p-2 font-medium text-muted-foreground">
                    {lComponents(row.labelKey, { defaultValue: row.labelDefault })}
                  </td>
                  {results.map((result) => (
                    <td 
                      key={result.locationId} 
                      className={cn(
                        'p-2 text-center',
                        row.isBest(result) && 'bg-green-50 dark:bg-green-950/20'
                      )}
                    >
                      {typeof row.getValue(result) === 'string' ? (
                        <span className={row.isBest(result) ? 'font-semibold text-green-600' : ''}>
                          {row.getValue(result)}
                        </span>
                      ) : (
                        row.getValue(result)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              
              {/* Highlights Row */}
              <tr>
                <td className="p-2 font-medium text-muted-foreground">
                  <LComponents id="tax_explorer.comparison.highlights" defaultValue='Highlights' />
                </td>
                {results.map((result) => (
                  <td key={result.locationId} className="p-2">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {result.highlights.slice(0, 3).map((h, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {h}
                        </Badge>
                      ))}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Current Tax Reference */}
        <div className={cn(
          RESPONSIVE.flexRowBetween,
          TABS.contentNested,
          'p-3 bg-muted/50 rounded-lg'
        )}>
          <span className={cn(TYPOGRAPHY.cardDescription)}>
            <LComponents
              id="tax_explorer.comparison.current_tax_burden"
              defaultValue='Current Tax Burden' />
          </span>
          <span className="font-semibold">{formatCompactCurrency(currentTax, EXPLORER_CURRENCY)}</span>
        </div>
      </CardContent>
    </Card>
  );
};